import { useCallback, useEffect, useRef, useState } from "react";
import { useWizard } from "@/hooks/useListingWizard";
import { useUser } from "@/hooks/useUser";
import { useToast } from "@/hooks/use-toast";
import { listingsApi } from "@/lib/listings-api";
import { PHOTO_MAX } from "@workspace/listing-requirements";
import {
  checkImageQuality,
  type EditorState,
  type LocalImage,
} from "@/lib/photo-processing";

/**
 * Shared upload / reorder / inline-edit engine for car listing photos.
 *
 * Both the listing wizard (StepPhotos) and the edit gallery
 * (EditPhotosGallery) drive identical Algolia-agnostic photo logic: the
 * temp-upload optimistic flow, the editor queue, drag reordering and the
 * primary/delete operations. This hook owns all of that. The two consumers
 * keep their own JSX and surface-specific UI state — warnings + touch
 * reordering for the wizard; the lightbox + delete-confirm modal for the
 * gallery — and wrap `handleDelete` to do their own post-success cleanup.
 */
export function useCarPhotoManager({
  images,
  setImages,
  onUploadingChange,
  keepFailedUploads = false,
}: {
  images: LocalImage[];
  setImages: React.Dispatch<React.SetStateAction<LocalImage[]>>;
  onUploadingChange?: (uploading: boolean) => void;
  /**
   * Task #6708 — wizard only: keep a failed upload in the grid as an
   * explicit "Upload failed" tile with a retry button instead of removing
   * it. The edit gallery keeps the legacy remove-and-toast behavior (it
   * has no failed-tile UI, so a kept tile would masquerade as uploaded).
   */
  keepFailedUploads?: boolean;
}) {
  const { data, fieldErrors } = useWizard();
  const { session } = useUser();
  const { toast } = useToast();

  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [autoEnhanceCount, setAutoEnhanceCount] = useState(0);

  const fileRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const queueOrderRef = useRef(0);
  const recentDragRef = useRef(false);

  const carId = data.carId;
  const token = session?.access_token;

  const hasUploading = images.some(i => i.uploading);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderError, setOrderError] = useState(false);
  useEffect(() => {
    onUploadingChange?.(hasUploading || savingOrder || orderError);
  }, [hasUploading, savingOrder, orderError, onUploadingChange]);

  const imagesRef = useRef(images);
  imagesRef.current = images;
  const uploadsRef = useRef(0);
  const uploadWaitersRef = useRef<Array<() => void>>([]);
  const pendingOrderRef = useRef(false);
  const orderWorkerRef = useRef<Promise<void> | null>(null);
  const revisionRef = useRef(0);

  const commitImages = useCallback((next: LocalImage[]) => {
    imagesRef.current = next;
    setImages(next);
  }, [setImages]);
  const activeOrder = (list: LocalImage[]) =>
    list.filter(i => i.is_active !== false && !i.uploading && !i.failed && Number.isSafeInteger(i.id) && i.id > 0)
      .map(i => Number(i.id));
  const numbered = (list: LocalImage[]) =>
    list.map((img, index) => ({ ...img, display_order: index, is_primary: index === 0 }));

  // A reordered tile is only "saved" once the server has accepted the full
  // active set. A later edit replaces the queued order rather than racing a
  // second PUT against the first one. Uploads must settle before taking it.
  const flushOrder = useCallback((): Promise<void> => {
    if (orderWorkerRef.current) return orderWorkerRef.current;
    if (!carId || !token) return Promise.resolve();
    setSavingOrder(true);
    const worker = (async () => {
      while (pendingOrderRef.current) {
        if (uploadsRef.current > 0) {
          await new Promise<void>(resolve => uploadWaitersRef.current.push(resolve));
          continue;
        }
        pendingOrderRef.current = false;
        const order = activeOrder(imagesRef.current);
        if (!order.length) continue;
        const revision = revisionRef.current;
        try {
          await listingsApi.reorderPhotos(carId, token, order);
          const detail = await listingsApi.getListing(carId, token);
          if (revision === revisionRef.current) {
            const previews = new Map(imagesRef.current.map(i => [i.id, i.previewUrl]));
            const temporary = imagesRef.current.filter(i => i.failed || i.uploading);
            commitImages([...detail.images.map(i => ({ ...i, previewUrl: previews.get(i.id) || undefined })), ...temporary]);
            setOrderError(false);
          }
        } catch (err) {
          console.error("Photo reorder failed:", err);
          // Do not leave a locally selected cover labelled as saved. Read the
          // authoritative inventory even on 409; the owner can retry the
          // selection after the conflicting writer has finished.
          // Keep a newer selection queued if it was made while this
          // request was in flight; only the failed revision is discarded.
          if (revision === revisionRef.current) pendingOrderRef.current = false;
          setOrderError(true);
          try {
            const detail = await listingsApi.getListing(carId, token);
            if (revision === revisionRef.current) {
              commitImages([...detail.images, ...imagesRef.current.filter(i => i.failed || i.uploading)]);
            }
          } catch {
            // The server read failed too. Keep the uploaded tiles visible,
            // but leave the order explicitly unconfirmed until retry.
          }
          toast({ title: "Photo order not saved", description: "Could not save the cover photo. Check the photos and try again.", variant: "destructive" });
        }
      }
    })().finally(() => {
      orderWorkerRef.current = null;
      setSavingOrder(false);
      // A new request may have arrived while the worker was winding down.
      if (pendingOrderRef.current) void flushOrder();
    });
    orderWorkerRef.current = worker;
    return worker;
  }, [carId, token, commitImages, toast]);

  const persistReorder = useCallback(async () => {
    if (!carId || !token || activeOrder(imagesRef.current).length === 0) return;
    revisionRef.current++;
    pendingOrderRef.current = true;
    await flushOrder();
  }, [carId, token, flushOrder]);

  const finishUpload = useCallback(() => {
    uploadsRef.current--;
    if (uploadsRef.current === 0) {
      uploadWaitersRef.current.splice(0).forEach(wake => wake());
      // An upload can insert a row with a stale display_order or primary flag.
      // Always send the complete local order after its server ID is known.
      void persistReorder();
    }
  }, [persistReorder]);

  const uploadBlob = useCallback(async (blob: Blob, orderIndex: number, shouldBePrimary: boolean): Promise<{ id: number | string } | null> => {
    if (!carId || !token) return null;

    const previewUrl = URL.createObjectURL(blob);
    const tempId = Date.now() + Math.random();

    const tempImage: LocalImage = {
      id: tempId,
      url: previewUrl,
      r2_path: null,
      is_primary: shouldBePrimary,
      display_order: orderIndex,
      uploading: true,
      previewUrl,
    };

    uploadsRef.current++;
    commitImages([...imagesRef.current, tempImage]);

    try {
      const result = await listingsApi.uploadPhoto(carId, token, blob, {
        is_primary: shouldBePrimary,
        display_order: orderIndex,
      });

      commitImages(imagesRef.current.map(img =>
          img.id === tempId
            ? { ...result, uploading: false, previewUrl, display_order: img.display_order, is_primary: img.is_primary }
            : img
        ));
      revisionRef.current++;
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not upload photo";
      console.error("[useCarPhotoManager] Upload failed:", msg);
      if (keepFailedUploads) {
        // Keep the tile with an explicit failed state + the blob so the
        // user can retry in place without re-picking the file.
        commitImages(imagesRef.current.map(img =>
            img.id === tempId
              ? { ...img, uploading: false, failed: true, retryBlob: blob }
              : img
          ));
      } else {
        commitImages(imagesRef.current.filter(img => img.id !== tempId));
      }
      toast({ title: "Upload Failed", description: msg, variant: "destructive" });
      return null;
    } finally {
      finishUpload();
    }
  }, [carId, token, commitImages, toast, keepFailedUploads, finishUpload]);

  /**
   * Task #6708 — retry a failed upload in place. Re-uses the stored blob;
   * flips the tile back to `uploading` and swaps in the server row on
   * success, or back to `failed` (blob retained) on another failure.
   */
  const retryUpload = useCallback(async (photoId: number | string) => {
    if (!carId || !token) return;
    const target = imagesRef.current.find(img => img.id === photoId);
    if (!target?.failed || !target.retryBlob) return;
    const blob = target.retryBlob;
    const orderIndex = imagesRef.current.findIndex(img => img.id === photoId);
    uploadsRef.current++;
    commitImages(imagesRef.current.map(img => (img.id === photoId ? { ...img, uploading: true, failed: false } : img)));
    try {
      const result = await listingsApi.uploadPhoto(carId, token, blob, {
        is_primary: orderIndex === 0,
        display_order: Math.max(orderIndex, 0),
      });
      commitImages(imagesRef.current.map(img =>
          img.id === photoId
            ? { ...result, uploading: false, previewUrl: img.previewUrl, display_order: img.display_order, is_primary: img.is_primary }
            : img
        ));
      revisionRef.current++;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not upload photo";
      console.error("[useCarPhotoManager] Retry upload failed:", msg);
      commitImages(imagesRef.current.map(img =>
          img.id === photoId ? { ...img, uploading: false, failed: true, retryBlob: blob } : img
        ));
      toast({ title: "Upload Failed", description: msg, variant: "destructive" });
    } finally {
      finishUpload();
    }
  }, [carId, token, commitImages, toast, finishUpload]);

  /** Task #6708 — drop a failed tile the user doesn't want to retry. */
  const removeFailedUpload = useCallback((photoId: number | string) => {
    const target = imagesRef.current.find(img => img.id === photoId);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    commitImages(imagesRef.current.filter(img => img.id !== photoId));
  }, [commitImages]);

  const advanceEditorQueue = useCallback(() => {
    if (pendingFiles.length > 0) {
      const nextFile = pendingFiles[0];
      const remaining = pendingFiles.slice(1);
      setPendingFiles(remaining);
      queueOrderRef.current += 1;
      const nextOrder = queueOrderRef.current;
      const nextUrl = URL.createObjectURL(nextFile);
      setEditorState({
        imageSource: nextUrl,
        mode: "new",
        file: nextFile,
        orderIndex: nextOrder,
        shouldBePrimary: nextOrder === 0,
      });
    }
  }, [pendingFiles]);

  const advanceToNextImage = useCallback((currentIdx: number) => {
    const nextIdx = currentIdx + 1;
    const nextImg = images[nextIdx];
    if (nextImg && !nextImg.uploading) {
      const imageUrl = nextImg.previewUrl
        ? nextImg.previewUrl
        : nextImg.editor_proxy_url
          ? `${import.meta.env.BASE_URL || "/"}${nextImg.editor_proxy_url.replace(/^\//, "")}`
          : nextImg.url;
      if (imageUrl) {
        setEditorState({
          imageSource: imageUrl,
          mode: "edit",
          photoId: nextImg.id,
          orderIndex: nextIdx,
          shouldBePrimary: nextIdx === 0,
        });
        return;
      }
    }
    setEditorState(null);
  }, [images]);

  const handleEditorSave = useCallback(async (blob: Blob) => {
    if (!editorState || !carId || !token) return;

    // Audit fix (May 2026 photo pipeline audit): the editor already
    // caps its export at EDITOR_MAX_DIMENSION (2400px on the longest
    // edge) and emits JPEG at q=0.92. Running a second resize/re-encode
    // here was pure quality loss — the dominant contributor to the
    // "muddy gradient / contrast drift" hosts reported. We keep
    // `checkImageQuality` (a 100×100 brightness/sharpness heuristic, no
    // re-encode) but upload the editor's blob directly.
    const resizedBlob = blob;

    const qualityFile = new File([resizedBlob], "check.jpg", { type: resizedBlob.type || "image/jpeg" });
    const quality = await checkImageQuality(qualityFile);
    const rejectReasons: string[] = [];
    if (quality.dark) rejectReasons.push("Image is too dark");
    if (quality.small) rejectReasons.push("Image resolution is too low (min 800x450)");
    if (quality.blurry) rejectReasons.push("Image is too blurry");

    if (rejectReasons.length > 0) {
      if (editorState.mode === "new") URL.revokeObjectURL(editorState.imageSource);
      toast({
        title: "Photo rejected",
        description: rejectReasons.join(". "),
        variant: "destructive",
      });
      setEditorState(null);
      advanceEditorQueue();
      return;
    }

    const capturedEditor = editorState;
    if (capturedEditor.mode === "new") {
      URL.revokeObjectURL(capturedEditor.imageSource);
      const result = await uploadBlob(resizedBlob, capturedEditor.orderIndex, capturedEditor.shouldBePrimary);
      if (result) {
        if (pendingFiles.length > 0) {
          advanceEditorQueue();
        } else {
          setEditorState(null);
        }
      } else {
        setPendingFiles([]);
        setEditorState(null);
      }
    } else {
      const newPreviewUrl = URL.createObjectURL(resizedBlob);
      const photoId = capturedEditor.photoId;
      const editIdx = capturedEditor.orderIndex;

      uploadsRef.current++;
      commitImages(imagesRef.current.map(img =>
          img.id === photoId
            ? { ...img, uploading: true, previewUrl: newPreviewUrl }
            : img
        ));

      try {
        const result = await listingsApi.uploadPhoto(carId, token, resizedBlob, {
          is_primary: capturedEditor.shouldBePrimary,
          display_order: editIdx,
        });

        commitImages(imagesRef.current.map(img =>
            img.id === photoId
              ? { ...result, uploading: false, previewUrl: newPreviewUrl, display_order: img.display_order, is_primary: img.is_primary }
              : img
          ));
        revisionRef.current++;

        // Keep the replacement within the same upload barrier until the
        // old active row is removed. Otherwise a cover reorder can race
        // with two live versions of the same photo.
        await listingsApi.deletePhoto(carId, photoId, token);

        if (pendingFiles.length > 0) {
          advanceEditorQueue();
        } else {
          advanceToNextImage(editIdx);
        }
      } catch {
        // The new upload may already exist even if deletion failed.
        // Reconcile both active rows rather than pretending the old one
        // vanished or sending an incomplete order to the reorder RPC.
        try {
          const detail = await listingsApi.getListing(carId, token);
          commitImages(detail.images);
        } catch {
          commitImages(imagesRef.current.map(img =>
            img.id === photoId ? { ...img, uploading: false, previewUrl: undefined } : img
          ));
        }
        URL.revokeObjectURL(newPreviewUrl);
        toast({ title: "Update Failed", description: "Could not update photo", variant: "destructive" });
        setEditorState(null);
      } finally {
        finishUpload();
      }
    }
  }, [editorState, carId, token, commitImages, toast, uploadBlob, advanceEditorQueue, advanceToNextImage, pendingFiles.length, finishUpload]);

  const handleFiles = useCallback(async (files: FileList) => {
    // Task #6708 — never a silent stall: same fix class as the AI story
    // button (Task inbox, May 2026). A missing session or a draft that
    // hasn't created its carId yet used to swallow the file-pick with zero
    // feedback, which reads as a dead uploader.
    if (!token) {
      toast({
        title: "Please sign in again",
        description: "Your session has expired. Sign in and try again.",
        variant: "destructive",
      });
      return;
    }
    if (!carId) {
      toast({
        title: "Listing still saving",
        description: "We're finishing saving your listing — please try again in a moment.",
        variant: "destructive",
      });
      return;
    }

    const allFiles = Array.from(files);
    const toProcess = allFiles.filter(
      f => ["image/jpeg", "image/png", "image/webp"].includes(f.type) && f.size <= 20 * 1024 * 1024
    );

    const rejectedCount = allFiles.length - toProcess.length;
    if (rejectedCount > 0) {
      toast({
        title: `${rejectedCount} file${rejectedCount > 1 ? "s" : ""} skipped`,
        description: "Only JPEG, PNG, and WebP images under 20 MB are accepted.",
        variant: "destructive",
      });
    }

    const currentLength = images.length;
    const remaining = PHOTO_MAX - currentLength;
    const batch = toProcess.slice(0, remaining);

    if (toProcess.length > remaining) {
      // Inbox feedback (design topic, May 2026): the description used
      // to always read "Only N more photo(s) can be added (N max)".
      // When the user was already at the cap, that came out as
      // "Only 0 more photos can be added (N max)" — technically
      // correct, but reads as a glitch ("only 0…?"). Branch on the
      // remaining slots so the at-cap case gives an instruction
      // instead of a zero count.
      const description = remaining <= 0
        ? `You've already added the maximum of ${PHOTO_MAX} photos. Remove one to upload a different photo.`
        : `Only ${remaining} more photo${remaining !== 1 ? "s" : ""} can be added (${PHOTO_MAX} max).`;
      toast({
        title: "Photo limit reached",
        description,
        variant: "destructive",
      });
    }

    if (batch.length === 0) return;

    const firstFile = batch[0];
    const restFiles = batch.slice(1);
    setPendingFiles(restFiles);

    queueOrderRef.current = currentLength;
    const previewUrl = URL.createObjectURL(firstFile);
    setEditorState({
      imageSource: previewUrl,
      mode: "new",
      file: firstFile,
      orderIndex: currentLength,
      shouldBePrimary: currentLength === 0,
    });
  }, [carId, token, images.length, toast]);

  const handleCancelEditor = useCallback(() => {
    if (editorState) {
      URL.revokeObjectURL(editorState.imageSource);
    }
    setPendingFiles([]);
    setEditorState(null);
  }, [editorState]);

  const handleEditExisting = useCallback((img: LocalImage, idx: number) => {
    if (img.previewUrl) {
      setEditorState({
        imageSource: img.previewUrl,
        mode: "edit",
        photoId: img.id,
        orderIndex: idx,
        shouldBePrimary: idx === 0,
      });
      return;
    }
    const imageUrl = img.editor_proxy_url
      ? `${import.meta.env.BASE_URL || "/"}${img.editor_proxy_url.replace(/^\//, "")}`
      : img.url;
    if (!imageUrl) return;
    setEditorState({
      imageSource: imageUrl,
      mode: "edit",
      photoId: img.id,
      orderIndex: idx,
      shouldBePrimary: idx === 0,
    });
  }, []);

  /**
   * Deletes a photo and renumbers the remaining set. Returns `true` on
   * success so callers can run surface-specific cleanup (clear the
   * wizard warning entry, close the gallery confirm modal) only when the
   * delete actually landed.
   */
  const handleDelete = useCallback(async (photoId: number): Promise<boolean> => {
    if (!carId || !token) return false;
    try {
      await listingsApi.deletePhoto(carId, photoId, token);
      commitImages(numbered(imagesRef.current.filter(img => img.id !== photoId)));
      void persistReorder();
      return true;
    } catch (err) {
      toast({ title: "Delete Failed", description: err instanceof Error ? err.message : "Could not delete photo", variant: "destructive" });
      return false;
    }
  }, [carId, token, commitImages, persistReorder, toast]);

  const reorderImages = useCallback((fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    const arr = [...imagesRef.current];
    if (!arr[fromIdx] || !arr[toIdx] || arr[fromIdx].uploading || arr[fromIdx].failed) return;
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    revisionRef.current++;
    commitImages(numbered(arr));
  }, [commitImages]);

  const handleSetPrimary = useCallback(async (photoId: number) => {
    if (!carId || !token) return;
    const idx = imagesRef.current.findIndex(img => img.id === photoId && !img.uploading && !img.failed);
    if (idx <= 0) return;
    const arr = [...imagesRef.current];
    const [moved] = arr.splice(idx, 1);
    arr.unshift(moved);
    revisionRef.current++;
    commitImages(numbered(arr));
    await persistReorder();
  }, [carId, token, commitImages, persistReorder]);

  const handleDragStart = useCallback((idx: number) => setDragIdx(idx), []);
  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    reorderImages(dragIdx, idx);
    setDragIdx(idx);
  }, [dragIdx, reorderImages]);
  const handleDragEnd = useCallback(async () => {
    setDragIdx(null);
    recentDragRef.current = true;
    setTimeout(() => { recentDragRef.current = false; }, 300);
    await persistReorder();
  }, [persistReorder]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  return {
    carId,
    token,
    fieldErrors,
    // editor / upload state
    editorState,
    pendingFiles,
    autoEnhanceCount,
    setAutoEnhanceCount,
    hasUploading,
    savingOrder,
    orderError,
    // drag state
    dragIdx,
    setDragIdx,
    // refs
    fileRef,
    gridRef,
    recentDragRef,
    // handlers
    uploadBlob,
    retryUpload,
    removeFailedUpload,
    handleFiles,
    handleEditorSave,
    handleCancelEditor,
    handleEditExisting,
    handleDelete,
    handleSetPrimary,
    reorderImages,
    persistReorder,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDrop,
  };
}
